import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, WMSTileLayer, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { API_URL } from './config';

// Komponenty
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Hub from './components/Hub';
import ValveModal from './components/ValveModal';
import PipeNamingModal from './components/PipeNamingModal';
import PipeModal from './components/PipeModal';
import LocationModal from './components/LocationModal';
import MapLayerControl, { MapLayerType } from './components/MapLayerControl';
import { CheckCircle, AlertCircle } from 'lucide-react';

// Style
import './index.css';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

// --- KOMPONENT DO OBSŁUGI ZDARZEŃ MAPY ---
const MapEventsHandler = ({ onEmptyClick, onZoomChange, lastClosedTimeRef }: { onEmptyClick: () => void, onZoomChange: (z: number) => void, lastClosedTimeRef: React.MutableRefObject<number> }) => {
    const map = useMapEvents({
        click: (e: any) => {
            if (Date.now() - lastClosedTimeRef.current < 400) {
                return;
            }
            const target = e.originalEvent?.target as HTMLElement;
            if (target && (target.tagName === 'path' || target.tagName === 'circle' || target.closest('.leaflet-interactive') || target.closest('.custom-geoman-marker') || target.closest('.valve-modal-container') || target.closest('.pipe-modal-container'))) {
                return;
            }
            onEmptyClick();
        },
        zoomend: () => onZoomChange(map.getZoom()),
    });
    return null;
};

// --- LOGIKA GEOMAN ---
const GeomanLogic = ({ onPipeCreate, onValveCreate, onDeleted, onAction, onSave, user, showToast, activeOrg, drawMode }: any) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;
        map.pm.setLang('pl');

        // Konfiguracja nowoczesnej ikony dla zaworów (zamiast domyślnego niebieskiego markera)
        const isSUW = drawMode === 'suw';
        const customMarkerIcon = L.divIcon({
            className: 'custom-geoman-marker',
            html: isSUW
                ? `<div style="width: 14px; height: 14px; background-color: #10b981; border: 3px solid white; border-radius: 50%;"></div>`
                : `<div style="width: 14px; height: 14px; background-color: #3b82f6; border: 3px solid white; border-radius: 50%;"></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10]
        });

        // Włączanie snapowania do linii i ustawienie ikony
        map.pm.setGlobalOptions({
            snapDistance: 20,
            allowSelfIntersection: false,
            markerStyle: { icon: customMarkerIcon }
        });

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                const drawLine = (map.pm.Draw as any).Line;
                if (drawLine && drawLine.enabled()) drawLine._finishShape();
                if (map.pm.globalEditModeEnabled()) {
                    map.pm.disableGlobalEditMode();
                    onAction('pointer');
                }
            }
            if (e.key === 'Escape') {
                map.pm.disableDraw();
                map.pm.disableGlobalEditMode();
                map.pm.disableGlobalRemovalMode();
                onAction('pointer');
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        map.on('pm:create', (e: any) => {
            const { layer, shape } = e;
            if ((layer as any)._processed) return;

            const pos = shape === 'Line' ? layer.getLatLngs()[0] : layer.getLatLng();

            // Geofencing: używamy aktywnej gminy dla Superadmina lub przypisanej dla reszty
            const bounds = activeOrg || user.organization;

            if (bounds && bounds.min_lat) {
                const isOutOfBounds =
                    pos.lat < bounds.min_lat || pos.lat > bounds.max_lat ||
                    pos.lng < bounds.min_lng || pos.lng > bounds.max_lng;

                if (isOutOfBounds) {
                    showToast(`❌ Poza obszarem: ${bounds.name}`, true);
                    map.removeLayer(layer);
                    onAction('pointer');
                    return;
                }
            }

            (layer as any)._processed = true;
            if (shape === 'Line') {
                const coords = layer.getLatLngs().map((l: any) => [l.lat, l.lng]);
                onPipeCreate(coords);
            } else if (shape === 'Marker') {
                const coords = [layer.getLatLng().lat, layer.getLatLng().lng];
                onValveCreate(coords, drawMode === 'suw' ? 'suw' : 'valve');
            }
            map.removeLayer(layer);
            onAction('pointer');
        });

        map.on('pm:remove', async (e: any) => {
            const id = e.layer.feature?.properties?.id || e.layer.options?.id;
            const isPipe = e.layer instanceof L.Polyline && !(e.layer instanceof L.Polygon);
            const type = isPipe ? 'pipes' : 'valves';

            if (!id) { onSave(); return; }

            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/${type}/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                onDeleted(`Usunięto ${isPipe ? 'rurę' : 'obiekt'}`);
                onAction('pointer');
            } catch (err: any) {
                showToast("Nie udało się usunąć obiektu", true);
            }
            onSave();
        });

        // Edycja jest teraz podpięta bezpośrednio pod warstwy GeoJSON (onEachFeature)

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            map.off('pm:create');
            map.off('pm:remove');
        };
    }, [map, onPipeCreate, onValveCreate, onDeleted, onAction, onSave, user, showToast, activeOrg, drawMode]);

    return null;
};

const App = () => {
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [user, setUser] = useState<any>(null);
    const [data, setData] = useState<any>({ pipes: null, valves: null });
    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);

    // Zarządzanie widokami
    const [view, setView] = useState<'map' | 'admin' | 'hub'>('map');
    const [activeOrg, setActiveOrg] = useState<any>(null);

    const [toast, setToast] = useState<{ msg: string, isError: boolean } | null>(null);
    const [pipeNamingCoords, setPipeNamingCoords] = useState<any>(null);
    const [selectedValve, setSelectedValve] = useState<any>(null);
    const [selectedPipe, setSelectedPipe] = useState<any>(null);
    const selectedPipeRef = useRef<any>(null);
    useEffect(() => { selectedPipeRef.current = selectedPipe; }, [selectedPipe]);
    const [drawMode, setDrawMode] = useState<'pipe' | 'valve' | 'suw' | null>(null);

    const lastClosedModalTimeRef = useRef<number>(0);

    const closeModal = useCallback(() => {
        lastClosedModalTimeRef.current = Date.now();
        setSelectedValve(null);
        setSelectedPipe(null);
        setTempParentId(null);
        setIsPickingParent(false);
    }, []);

    const [isPickingParent, setIsPickingParent] = useState(false);
    const isPickingParentRef = useRef(false);
    useEffect(() => { isPickingParentRef.current = isPickingParent; }, [isPickingParent]);
    const [tempParentId, setTempParentId] = useState<number | null>(null);
    const [zoomLevel, setZoomLevel] = useState<number>(15);
    const [locationModalData, setLocationModalData] = useState<{ lat: number, lng: number, accuracy?: number } | null>(null);

    // Wybrana warstwa mapy (OSM / Geoportal Ortomapa / Esri Satelita)
    const [mapLayer, setMapLayer] = useState<MapLayerType>(() => {
        return (localStorage.getItem('aquaflow_map_layer') as MapLayerType) || 'osm';
    });

    const handleMapLayerChange = (layer: MapLayerType) => {
        setMapLayer(layer);
        localStorage.setItem('aquaflow_map_layer', layer);
    };

    // Środek mapy (zależy od wybranej w Hubie gminy lub organizacji usera)
    const mapCenter: [number, number] = activeOrg
        ? [activeOrg.latitude, activeOrg.longitude]
        : (user && user.organization ? [user.organization.latitude, user.organization.longitude] : [53.4544, 18.9610]);

    const loadData = useCallback(async () => {
        if (!token) return;
        const config = { headers: { Authorization: `Bearer ${token}` } };
        try {
            const userRes = await axios.get(`${API_URL}/auth/me`, config);
            const u = userRes.data;
            setUser(u);

            // Flow dla Superadmina: jeśli nie wybrał gminy, pokazujemy Hub
            if (u.role === 'superadmin' && !activeOrg && view === 'map') {
                setView('hub');
            }

            const [p, v] = await Promise.all([
                axios.get(`${API_URL}/pipes`, config),
                axios.get(`${API_URL}/valves`, config)
            ]);
            setData({ pipes: p.data, valves: v.data });
        } catch (err) { handleLogout(); }
    }, [token, activeOrg, view]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setActiveOrg(null);
        setView('map');
    };

    const showToast = (msg: string, isError: boolean = false) => {
        setToast({ msg, isError });
        setTimeout(() => setToast(null), 3000);
    };

    const handleAction = (action: string) => {
        if (!mapInstance) return;
        if (mapInstance.pm.Draw && (mapInstance.pm.Draw as any).getActiveShape && (mapInstance.pm.Draw as any).getActiveShape()) {
            mapInstance.pm.disableDraw();
        }
        if (mapInstance.pm.globalEditModeEnabled()) {
            mapInstance.pm.disableGlobalEditMode();
        }
        if (mapInstance.pm.globalRemovalModeEnabled()) {
            mapInstance.pm.disableGlobalRemovalMode();
        }

        if (action === 'zoomIn') {
            mapInstance.zoomIn();
        } else if (action === 'zoomOut') {
            mapInstance.zoomOut();
        } else if (action === 'drawPolyline') {
            setDrawMode('pipe');
            mapInstance.pm.enableDraw('Line');
        } else if (action === 'drawMarker') {
            setDrawMode('valve');
            mapInstance.pm.enableDraw('Marker');
        } else if (action === 'drawSUW') {
            setDrawMode('suw');
            mapInstance.pm.enableDraw('Marker');
        } else if (action === 'edit') {
            setDrawMode(null);
            mapInstance.pm.enableGlobalEditMode();
        } else if (action === 'delete') {
            setDrawMode(null);
            mapInstance.pm.enableGlobalRemovalMode();
        } else if (action === 'locate') {
            setDrawMode(null);
            if ("geolocation" in navigator) {
                showToast("Szukanie dokładnej lokalizacji GPS...");
                let bestPos: GeolocationPosition | null = null;
                let watchId: number;
                let timeoutId: ReturnType<typeof setTimeout>;

                const finish = (pos: GeolocationPosition) => {
                    if (watchId) navigator.geolocation.clearWatch(watchId);
                    if (timeoutId) clearTimeout(timeoutId);
                    
                    const { latitude, longitude, accuracy } = pos.coords;
                    const latlng = new L.LatLng(latitude, longitude);
                    mapInstance.setView(latlng, 17);
                    
                    L.circleMarker(latlng, { radius: 8, fillColor: '#3b82f6', color: 'white', weight: 3, fillOpacity: 1 }).addTo(mapInstance);
                    showToast(`Zlokalizowano (dokładność: ${Math.round(accuracy)}m)`);
                    setLocationModalData({ lat: latitude, lng: longitude, accuracy: accuracy });
                };

                watchId = navigator.geolocation.watchPosition(
                    (position) => {
                        // Aktualizujemy najlepszą pozycję
                        if (!bestPos || position.coords.accuracy < bestPos.coords.accuracy) {
                            bestPos = position;
                            showToast(`GPS: dokładność ${Math.round(position.coords.accuracy)}m...`);
                        }
                        
                        // Zadowalamy się wynikiem, jeśli dokładność jest <= 30 metrów
                        if (position.coords.accuracy <= 30) {
                            finish(position);
                        }
                    },
                    (error) => {
                        if (!bestPos) {
                            showToast("Błąd geolokalizacji: " + error.message, true);
                            if (watchId) navigator.geolocation.clearWatch(watchId);
                            if (timeoutId) clearTimeout(timeoutId);
                        }
                    },
                    { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
                );

                // Po 8 sekundach przerywamy nasłuchiwanie i bierzemy najlepszy dotychczasowy wynik
                timeoutId = setTimeout(() => {
                    if (watchId) navigator.geolocation.clearWatch(watchId);
                    if (bestPos) {
                        finish(bestPos);
                    } else {
                        showToast("Nie udało się uzyskać pozycji GPS", true);
                    }
                }, 8000);

            } else {
                showToast("Geolokalizacja nie jest wspierana w tej przeglądarce", true);
            }
        } else {
            setDrawMode(null);
        }
    };

    const savePipe = async (name: string) => {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        try {
            await axios.post(`${API_URL}/pipes`, {
                name, coords: pipeNamingCoords, parent_id: tempParentId, org_id: activeOrg?.id
            }, config);
            setPipeNamingCoords(null);
            setTempParentId(null);
            showToast("Dodano rurę: " + name);
            loadData();
        } catch (err: any) {
            showToast(err.response?.data?.detail || "Błąd zapisu", true);
        }
    };

    if (!token) return <Login onLoginSuccess={(t) => setToken(t)} />;
    if (!user) return <div className="loading-screen">Wodociągi Gruta GIS...</div>;

    // --- LOGIKA WIDOKÓW ---

    // WIDOK 1: HUB WYBORU GMIN (Tylko dla Superadmina)
    if (view === 'hub') {
        return (
            <div className="admin-page-container">
                <div className="admin-top-nav">
                    <button onClick={handleLogout} className="nav-btn-logout">Wyloguj ({user.email})</button>
                </div>
                <Hub onSelect={(org: any) => { setActiveOrg(org); setView('admin'); }} />
            </div>
        );
    }

    // WIDOK 2: PANEL ADMINA (Dashboard wybranej gminy)
    if (view === 'admin') {
        return (
            <div className="admin-page-container">
                <div className="admin-top-nav">
                    {user.role === 'superadmin' && (
                        <button onClick={() => setView('hub')} className="nav-btn-admin">Zmień Gminę</button>
                    )}
                    <button onClick={handleLogout} className="nav-btn-logout">Wyloguj</button>
                </div>
                <Dashboard activeOrg={activeOrg} onGoToMap={() => setView('map')} />
            </div>
        );
    }

    // WIDOK 3: MAPA (Główny GIS)
    return (
        <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>

            {toast && (
                <div className="toast-container" style={{ border: toast.isError ? '1px solid #ef4444' : '1px solid #333' }}>
                    {toast.isError ? <AlertCircle color="#ef4444" size={20} /> : <CheckCircle color="#22c55e" size={20} />}
                    <span style={{ color: toast.isError ? '#ef4444' : 'white' }}>{toast.msg}</span>
                </div>
            )}

            {pipeNamingCoords && (
                <PipeNamingModal
                    onConfirm={savePipe}
                    onCancel={() => { setPipeNamingCoords(null); setTempParentId(null); }}
                    onStartPickingParent={() => setIsPickingParent(true)}
                    parentId={tempParentId}
                    isPicking={isPickingParent}
                />
            )}

            {locationModalData && (
                <LocationModal
                    lat={locationModalData.lat}
                    lng={locationModalData.lng}
                    accuracy={locationModalData.accuracy}
                    onClose={() => setLocationModalData(null)}
                    onAddValve={async () => {
                        const coords = [locationModalData.lat, locationModalData.lng];
                        try {
                            const config = { headers: { Authorization: `Bearer ${token}` } };
                            await axios.post(`${API_URL}/valves`, { coords, org_id: activeOrg?.id }, config);
                            showToast("Dodano zawór w Twojej lokalizacji");
                            setLocationModalData(null);
                            loadData();
                        } catch (err: any) {
                            showToast(err.response?.data?.detail || "Błąd", true);
                        }
                    }}
                />
            )}

            {/* POPRAWIONA NAWIGACJA: Strażnik Ról */}
            <div className="nav-btn-container">
                {user.role === 'superadmin' && activeOrg && (
                    <span style={{ backgroundColor: '#22c55e', color: 'white', padding: '6px 12px', borderRadius: '8px', fontSize: '14px', fontWeight: 600 }}>
                        Edytujesz: {activeOrg.name}
                    </span>
                )}

                {/* Tylko Ty widząc przycisk powrotu do panelu */}
                {user.role === 'superadmin' && (
                    <button onClick={() => setView('admin')} className="nav-btn-admin">
                        Powrót do Panelu
                    </button>
                )}

                <button onClick={handleLogout} className="nav-btn-logout">
                    Wyloguj ({user.email})
                </button>
            </div>

            {(user.role === 'superadmin' || user.role === 'admin' || user.role === 'designer') && (
                <div className="zoom-level-badge">
                    Zoom: {zoomLevel}
                </div>
            )}

            <Sidebar onAction={handleAction} role={user.role} />

            {/* Przełącznik widoku mapy (Standardowa / Ortomapa / Satelita) */}
            <MapLayerControl currentLayer={mapLayer} onLayerChange={handleMapLayerChange} />

            {selectedValve && (
                <ValveModal
                    key={selectedValve.id}
                    valve={selectedValve} role={user.role} 
                    onClose={closeModal}
                    pipes={data.pipes}
                    onToggle={async (id: number) => {
                        closeModal();
                        try {
                            await axios.post(`${API_URL}/valves/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
                            loadData();
                            showToast("Zmieniono stan");
                        } catch (err: any) {
                            showToast(err.response?.data?.detail || "Błąd zmiany stanu", true);
                        }
                    }}
                    onUpdate={async (id: number, payload: any) => {
                        closeModal();
                        try {
                            await axios.put(`${API_URL}/valves/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                            loadData();
                            showToast("Zapisano");
                        } catch (err: any) {
                            showToast(err.response?.data?.detail || "Błąd zapisu", true);
                        }
                    }}
                />
            )}

            {!selectedValve && selectedPipe && (
                <PipeModal
                    key={selectedPipe.id}
                    pipe={selectedPipe} role={user.role} parentId={tempParentId} isPicking={isPickingParent}
                    onStartPickingParent={() => setIsPickingParent(true)}
                    onClose={closeModal}
                    onUpdate={async (id: number, payload: any) => {
                        closeModal();
                        try {
                            await axios.put(`${API_URL}/pipes/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                            loadData();
                            showToast("Zapisano");
                        } catch (err: any) {
                            showToast(err.response?.data?.detail || "Błąd zapisu", true);
                        }
                    }}
                />
            )}

            <MapContainer key={activeOrg?.id || 'default'} center={mapCenter} zoom={zoomLevel} minZoom={12} maxZoom={22} zoomControl={false} style={{ height: '100%', width: '100%' }} ref={setMapInstance}>
                {mapLayer === 'osm' && (
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        maxNativeZoom={19}
                        maxZoom={22}
                    />
                )}
                {mapLayer === 'geoportal' && (
                    <>
                        <TileLayer
                            url="/tiles/{z}/{x}/{y}.jpg"
                            attribution='&copy; Główny Urząd Geodezji i Kartografii (Lokalna Ortomapa)'
                            minZoom={12}
                            maxNativeZoom={18}
                            maxZoom={22}
                        />
                        {zoomLevel >= 19 && (
                            <WMSTileLayer
                                url="https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/HighResolution"
                                layers="Raster"
                                format="image/jpeg"
                                attribution='&copy; Główny Urząd Geodezji i Kartografii (GUGiK Live)'
                                minZoom={19}
                                maxNativeZoom={20}
                                maxZoom={22}
                                transparent={true}
                            />
                        )}
                    </>
                )}
                {mapLayer === 'satellite' && (
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                        maxNativeZoom={19}
                        maxZoom={22}
                    />
                )}
                <MapEventsHandler onEmptyClick={closeModal} onZoomChange={(z) => setZoomLevel(z)} lastClosedTimeRef={lastClosedModalTimeRef} />
                <GeomanLogic
                    onAction={handleAction}
                    drawMode={drawMode}
                    onPipeCreate={(coords: any) => setPipeNamingCoords(coords)}
                    onValveCreate={async (coords: any, valveType: string = 'valve') => {
                        try {
                            const isSUW = valveType === 'suw';
                            const config = { headers: { Authorization: `Bearer ${token}` } };
                            await axios.post(`${API_URL}/valves`, {
                                coords,
                                valve_type: isSUW ? 'suw' : 'valve',
                                custom_color: isSUW ? '#10b981' : null,
                                name: isSUW ? 'Nowa Stacja SUW' : null,
                                org_id: activeOrg?.id
                            }, config);
                            showToast(isSUW ? "Dodano Stację Uzdatniania Wody (SUW)" : "Dodano zawór");
                            loadData();
                        } catch (err: any) { showToast(err.response?.data?.detail || "Błąd", true); }
                    }}
                    onDeleted={(msg: string) => showToast(msg)} onSave={loadData} user={user} showToast={showToast} activeOrg={activeOrg}
                />

                {data.pipes && (
                    <GeoJSON
                        key={`p-${JSON.stringify(data.pipes)}`}
                        data={data.pipes}
                        style={(f) => {
                            const isOpen = f?.properties.status === 'open';
                            const pipeColor = isOpen ? (f?.properties.color || '#3b82f6') : '#ef4444';
                            return {
                                color: pipeColor,
                                weight: zoomLevel > 14 ? 6 : 3,
                                opacity: 0.85,
                                dashArray: f?.properties.is_sp ? '10, 10' : undefined
                            };
                        }}
                        onEachFeature={(feature, layer: any) => {
                            layer.on('click', (e: any) => {
                                if (Date.now() - lastClosedModalTimeRef.current < 400) {
                                    return;
                                }
                                if (e?.originalEvent) {
                                    L.DomEvent.stopPropagation(e.originalEvent);
                                }
                                if (mapInstance?.pm.globalRemovalModeEnabled() || mapInstance?.pm.globalEditModeEnabled() || (mapInstance?.pm.Draw as any)?.getActiveShape()) return;

                                if (isPickingParentRef.current) {
                                    if (selectedPipeRef.current && selectedPipeRef.current.id === feature.properties.id) { showToast("Błąd: Rura nie może być własnym rodzicem!", true); return; }
                                    setTempParentId(feature.properties.id); setIsPickingParent(false); return;
                                }

                                setSelectedValve(null); 
                                setTempParentId(feature.properties.parent_id);
                                setSelectedPipe({ 
                                    id: feature.properties.id, 
                                    name: feature.properties.name, 
                                    description: feature.properties.description || '', 
                                    status: feature.properties.status, 
                                    parent_id: feature.properties.parent_id,
                                    is_sp: feature.properties.is_sp
                                });
                            });

                            layer.on('pm:edit', async () => {
                                const id = feature.properties.id;
                                if (!id) return;
                                const token = localStorage.getItem('token');
                                try {
                                    const coords = layer.getLatLngs().map((l: any) => [l.lat, l.lng]);
                                    await axios.put(`${API_URL}/pipes/${id}`, { coords }, { headers: { Authorization: `Bearer ${token}` } });
                                    showToast("Zapisano przebieg rury");
                                } catch (err) {
                                    showToast("Błąd zapisu rury", true);
                                }
                            });
                        }}
                    />
                )}

                {zoomLevel >= 12 && (user.role === 'superadmin' || user.role === 'admin' || user.role === 'designer' || user.role === 'worker') && data.valves && (
                    <GeoJSON
                        key={`v-${JSON.stringify(data.valves)}`}
                        data={data.valves}
                        pointToLayer={(f, ll) => {
                            if (f.properties.valve_type === 'suw') {
                                const suwColor = f.properties.is_closed ? '#ef4444' : (f.properties.custom_color || '#10b981');
                                return L.circleMarker(ll, {
                                    radius: 11,
                                    fillColor: suwColor,
                                    color: '#ffffff',
                                    weight: 3,
                                    fillOpacity: 1
                                });
                            }
                            return L.circleMarker(ll, {
                                radius: 9,
                                fillColor: f.properties.is_closed ? "#ef4444" : "#22c55e",
                                color: "white",
                                weight: 3,
                                fillOpacity: 1
                            });
                        }}
                        onEachFeature={(feature, layer: any) => {
                            layer.on('click', (e: any) => {
                                if (Date.now() - lastClosedModalTimeRef.current < 400) {
                                    return;
                                }
                                if (e?.originalEvent) {
                                    L.DomEvent.stopPropagation(e.originalEvent);
                                }
                                if (mapInstance?.pm.globalRemovalModeEnabled() || mapInstance?.pm.globalEditModeEnabled() || (mapInstance?.pm.Draw as any)?.getActiveShape()) return;

                                if (isPickingParentRef.current) return;

                                const geomCoords = (feature.geometry as any)?.coordinates;
                                const valveLat = geomCoords ? geomCoords[1] : e.latlng.lat;
                                const valveLng = geomCoords ? geomCoords[0] : e.latlng.lng;

                                setSelectedPipe(null);
                                setSelectedValve({
                                    id: feature.properties.id,
                                    name: feature.properties.name,
                                    is_closed: feature.properties.is_closed,
                                    description: feature.properties.description || '',
                                    valve_type: feature.properties.valve_type || 'valve',
                                    custom_color: feature.properties.custom_color || '#10b981',
                                    lat: valveLat,
                                    lng: valveLng,
                                    pipe_ids: feature.properties.pipe_ids || []
                                });
                            });

                            layer.on('pm:edit', async () => {
                                const id = feature.properties.id;
                                if (!id) return;
                                const token = localStorage.getItem('token');
                                try {
                                    const ll = layer.getLatLng();
                                    await axios.put(`${API_URL}/valves/${id}`, { lat: ll.lat, lng: ll.lng }, { headers: { Authorization: `Bearer ${token}` } });
                                    showToast("Zapisano nową pozycję obiektu");
                                } catch (err) {
                                    showToast("Błąd zapisu obiektu", true);
                                }
                            });
                        }}
                    />
                )}
            </MapContainer>
        </div>
    );
};

export default App;